import { useEffect, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/plans", label: "Plans" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthed, customer, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSignOut = () => {
    signOut();
    toast.success("Signed out");
    navigate("/");
    setOpen(false);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled
          ? "shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_-12px_rgba(15,23,42,0.06)]"
          : "border-b border-slate-100"
      }`}
    >
      <div className="container-page flex h-20 items-center justify-between gap-3 lg:gap-6">
        <Link
          to="/"
          aria-label="Silky Smooth Trash Bin Service. Home"
          className="flex items-center gap-3"
          data-testid="nav-logo-link"
        >
          <img
            src="/logo-wordmark.png"
            alt="Silky Smooth Trash Bin Service"
            className="h-9 sm:h-10 lg:h-12 w-auto select-none max-w-full"
            draggable={false}
          />
        </Link>

        <nav
          className="hidden md:flex items-center gap-5 lg:gap-9"
          aria-label="Primary"
          data-testid="nav-desktop-links"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 lg:gap-4">
          {isAuthed ? (
            <>
              <Link
                to="/portal"
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-ink transition-colors"
                data-testid="nav-myaccount-link"
              >
                <User size={14} />
                {customer?.full_name?.split(" ")[0] || "My Account"}
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="btn-primary"
                data-testid="nav-signout-button"
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/signin"
                className="text-sm font-medium text-slate-700 hover:text-ink transition-colors"
                data-testid="nav-signin-link"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="btn-primary"
                data-testid="nav-signup-button"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((s) => !s)}
          className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl border border-slate-200 text-ink hover:border-brand hover:text-brand transition-colors active:scale-95"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer. Slides in from the right with backdrop fade. */}
      <div
        className={`md:hidden fixed inset-0 z-40 ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!open}
        data-testid="nav-mobile-drawer"
      >
        <div
          onClick={() => setOpen(false)}
          aria-hidden="true"
          className={`absolute inset-0 bg-ink/40 backdrop-blur-sm transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        <aside
          className={`absolute top-0 right-0 h-screen w-[88vw] max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-20 flex items-center justify-between px-5 border-b border-slate-100">
            <img
              src="/logo-wordmark.png"
              alt="Silky Smooth Trash Bin Service"
              className="h-9 w-auto select-none"
              draggable={false}
            />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 text-ink hover:border-brand hover:text-brand transition-colors active:scale-95"
              data-testid="nav-mobile-close"
            >
              <X size={18} />
            </button>
          </div>

          <nav
            className="flex-1 overflow-y-auto px-5 py-4 flex flex-col"
            aria-label="Mobile primary"
          >
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between py-4 border-b border-slate-100 text-base font-semibold transition-colors ${
                    isActive ? "text-brand" : "text-ink hover:text-brand"
                  }`
                }
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span>{l.label}</span>
                <ArrowRight size={16} className="text-brand" />
              </NavLink>
            ))}

            <div className="mt-6 flex flex-col gap-3 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {isAuthed ? (
                <>
                  <Link
                    to="/portal"
                    className="btn-secondary w-full"
                    data-testid="nav-mobile-myaccount-link"
                  >
                    <User size={14} />
                    {customer?.full_name?.split(" ")[0] || "My Account"}
                  </Link>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="btn-primary w-full"
                    data-testid="nav-mobile-signout-button"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signin"
                    className="btn-secondary w-full"
                    data-testid="nav-mobile-signin-link"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="btn-primary w-full"
                    data-testid="nav-mobile-signup-button"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </nav>
        </aside>
      </div>
    </header>
  );
}
